import type { BlsPath, BlsState } from "./blsChannel";

/**
 * The safe range of speeds, in full cycles per minute.
 *
 * The ceiling (≈1.2 Hz) keeps even the edge-to-edge rhythm well under the
 * 3 Hz flash threshold photosensitivity guidance uses — and the dot never
 * flashes in the first place. The floor is slower than any protocol asks for.
 */
export const MIN_CYCLES_PER_MINUTE = 20;
export const MAX_CYCLES_PER_MINUTE = 70;
export const DEFAULT_CYCLES_PER_MINUTE = 40;

export function clampCyclesPerMinute(value: number): number {
    if (!Number.isFinite(value)) return DEFAULT_CYCLES_PER_MINUTE;
    return Math.min(MAX_CYCLES_PER_MINUTE, Math.max(MIN_CYCLES_PER_MINUTE, value));
}

/**
 * How many cycles a set has completed *now*, given a state that arrived a
 * moment ago.
 *
 * Both timestamps are the receiver's clock — when the packet landed, and now
 * — never the sender's `atEpochMs`: two laptops' clocks are not the same
 * clock, and one a second out would put the dot a second out with it.
 */
export function cyclesAt(state: BlsState, receivedAtEpochMs: number, nowEpochMs: number): number {
    if (!state.running) return state.cycles;
    const elapsedSeconds = Math.max(0, (nowEpochMs - receivedAtEpochMs) / 1000);
    const rate = clampCyclesPerMinute(state.settings.cyclesPerMinute) / 60;
    return state.cycles + elapsedSeconds * rate;
}

/** A point in the unit square: x from left to right, y from top to bottom. */
export type DotPoint = { x: number; y: number };

/**
 * Where the dot is at a given number of cycles.
 *
 * Sinusoidal rather than linear: the dot slows into each edge and speeds
 * through the middle, which is easier to follow and has none of the abrupt
 * reversals of a bouncing sweep. Every path is at the left edge on whole
 * cycles and the right edge on halves, so the tones land at the same moments
 * whichever path is chosen.
 */
export function dotPosition(path: BlsPath, cycles: number): DotPoint {
    const angle = 2 * Math.PI * cycles;
    const x = (1 - Math.cos(angle)) / 2;
    switch (path) {
        case "horizontal":
            return { x, y: 0.5 };
        case "diagonalUp":
            return { x, y: 1 - x };
        case "diagonalDown":
            return { x, y: x };
        case "infinity":
            // Crosses the middle line at the edges and the centre, so the
            // loops sit either side of it.
            return { x, y: 0.5 + 0.25 * Math.sin(2 * angle) };
    }
}

export type BlsEdge = "left" | "right";

/**
 * Which edges the dot reached between two cycle counts, in order.
 *
 * Half cycles are the right edge, whole ones the left. The starting point is
 * excluded — a set begins *at* the left edge and should not open on a tone —
 * and the end is included, so consecutive frames never report an edge twice.
 */
export function edgesCrossed(fromCycles: number, toCycles: number): BlsEdge[] {
    if (!(toCycles > fromCycles)) return [];
    const edges: BlsEdge[] = [];
    for (let half = Math.floor(fromCycles * 2) + 1; half <= toCycles * 2; half++) {
        edges.push(half % 2 === 0 ? "left" : "right");
    }
    return edges;
}
